/**
 * Review Helper - Utility for managing content reviews
 * Handles API parameter building, local caching, and content type detection
 */

/**
 * Check if content is an episode (series or program)
 * @param {Object} contentData - Raw content data
 * @returns {boolean}
 */
export function isContentEpisode(contentData) {
    if (!contentData) return false;

    var kind = contentData.contentType?.kind?.toLowerCase();
    return kind === 'series' || kind === 'program';
}

/**
 * Build query parameters for review API calls
 * @param {Object} contentData - Raw content data
 * @param {number} season - Season number (for episodes)
 * @param {number} episode - Episode number (for episodes)
 * @returns {Object} Query parameters
 */
export function buildReviewQueryParams(contentData, season, episode) {
    var params = {};

    if (!contentData || !contentData.slug) {
        console.warn('[ReviewHelper] Cannot build params: missing content slug');
        return params;
    }

    params.contentKey = contentData.slug;

    var isEpisode = isContentEpisode(contentData);
    params.isEpisode = isEpisode ? '1' : '0';

    // API expects season/episode params even for movies (default to 1)
    if (season && isEpisode) {
        params.season = String(season);
    }

    if (episode && isEpisode) {
        params.episode = String(episode);
    }

    return params;
}

/**
 * Build cache key for local storage
 * @param {string} contentKey - Content slug
 * @param {boolean} isEpisode - Whether content is episode
 * @param {number} season - Season number
 * @param {number} episode - Episode number
 * @returns {string} Cache key
 */
function buildCacheKey(contentKey, isEpisode, season, episode) {
    var key = 'review_cache_' + contentKey;

    if (isEpisode && season && episode) {
        key += '_s' + season + '_e' + episode;
    }

    return key;
}

/**
 * Cache review locally after submission
 * @param {string} contentKey - Content slug
 * @param {number} reviewType - 1=like, 2=dislike
 * @param {boolean} isEpisode - Whether content is episode
 * @param {number} season - Season number
 * @param {number} episode - Episode number
 */
export function cacheReview(contentKey, reviewType, isEpisode, season, episode) {
    try {
        var cacheKey = buildCacheKey(contentKey, isEpisode, season, episode);
        localStorage.setItem(cacheKey, String(reviewType));
        console.log('[ReviewHelper] Cached review:', cacheKey, '=', reviewType);
    } catch (error) {
        console.error('[ReviewHelper] Error caching review:', error);
    }
}

/**
 * Get cached review (to avoid API calls)
 * @param {string} contentKey - Content slug
 * @param {boolean} isEpisode - Whether content is episode
 * @param {number} season - Season number
 * @param {number} episode - Episode number
 * @returns {number|null} Review type (1=like, 2=dislike) or null if not cached
 */
export function getCachedReview(contentKey, isEpisode, season, episode) {
    try {
        var cacheKey = buildCacheKey(contentKey, isEpisode, season, episode);
        var cached = localStorage.getItem(cacheKey);

        if (cached !== null) {
            var value = parseInt(cached, 10);
            if (!isNaN(value)) {
                console.log('[ReviewHelper] Found cached review:', cacheKey, '=', value);
                return value;
            }
        }
    } catch (error) {
        console.error('[ReviewHelper] Error getting cached review:', error);
    }

    return null;
}

/**
 * Clear cached review after deletion
 * @param {string} contentKey - Content slug
 * @param {boolean} isEpisode - Whether content is episode
 * @param {number} season - Season number
 * @param {number} episode - Episode number
 */
export function clearCachedReview(contentKey, isEpisode, season, episode) {
    try {
        var cacheKey = buildCacheKey(contentKey, isEpisode, season, episode);
        localStorage.removeItem(cacheKey);
        console.log('[ReviewHelper] Cleared cached review:', cacheKey);
    } catch (error) {
        console.error('[ReviewHelper] Error clearing cached review:', error);
    }
}

/**
 * Clear all review caches
 */
export function clearAllReviewCaches() {
    try {
        var cleared = 0;
        var keys = Object.keys(localStorage);

        for (var i = 0; i < keys.length; i++) {
            var key = keys[i];
            if (key.startsWith('review_cache_')) {
                localStorage.removeItem(key);
                cleared++;
            }
        }

        console.log('[ReviewHelper] Cleared', cleared, 'review cache(s)');
    } catch (error) {
        console.error('[ReviewHelper] Error clearing all review caches:', error);
    }
}

/**
 * Check if review should be shown for content type
 * @param {Object} contentData - Raw content data
 * @returns {boolean}
 */
export function shouldShowReviewForContent(contentData) {
    if (!contentData) return false;

    // Disable for livestreams
    if (contentData.isLive === 1 || contentData.isLive === true) {
        return false;
    }

    // Disable for AnaVatan content
    if (
        contentData.isAnaVatan === 1 ||
        contentData.isAnaVatan === true ||
        contentData.anaVatanCity ||
        contentData.anaVatanProvince
    ) {
        return false;
    }

    // Disable for music content
    var kind = contentData.contentType?.kind?.toLowerCase();
    if (kind === 'music') {
        return false;
    }

    return true;
}
